// frontend/src/components/ProtectedRoute.jsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getMe, getStoredToken } from '../services/authApi';

const ProtectedRoute = ({ children }) => {
  const { user, setUser, isAuthReady } = useContext(AuthContext);
  const location = useLocation();

  // 1) Estado inicial súper rápido desde localStorage (sin UI de carga)
  const initialUserRef = useRef(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  });
  const [redirect, setRedirect] = useState(false);

  // 2) Si tengo user en contexto o en localStorage y un token (aunque esté por expirar),
  //    renderizo optimista SIN mostrar loader.
  const optimisticUser = user || initialUserRef.current();
  const hasSomeSession =
    isAuthReady && !!optimisticUser && !!getStoredToken();

  // 3) En segundo plano valido / renuevo. Si falla, recién ahí redirijo.
  useEffect(() => {
    let alive = true;

    const verify = async () => {
      if (!isAuthReady) return;
      // Si no hay absolutamente nada local, no mostramos pantalla protegida
      if (!hasSomeSession) {
        setRedirect(true);
        return;
      }

      try {
        const data = await getMe(); // esto intentará refresh si hace falta
        if (!alive) return;

        if (data?.authenticated) {
          setUser?.(data.user);
          try { localStorage.setItem('user', JSON.stringify(data.user)); } catch {}
        } else {
          // Terminal refresh rejection is cleaned up by the centralized flow.
          // A remaining token means refresh did not conclusively invalidate the
          // session, so keep the optimistic route instead of splitting storage
          // and navigation state.
          if (!getStoredToken()) setRedirect(true);
        }
      } catch (err) {
        // Preserve the optimistic session for transient or endpoint-level errors.
        // A terminal refresh rejection has already removed the stored token.
        if (alive && !getStoredToken()) setRedirect(true);
      }
    };

    // Disparamos siempre: confirma/renueva en background
    verify();

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAuthReady]); // revalida si cambia de ruta protegida

  // 4) Si determinamos que no hay sesión, redirigimos. Sino, renderizamos sin flash.
  if (!isAuthReady) return null;

  if (redirect) {
    return <Navigate to="/login" replace />;
  }

  // Render optimista: si hay algo local, mostramos la pantalla; si no hay nada, mandamos a login.
  return hasSomeSession ? children : <Navigate to="/login" replace />;
};

export default ProtectedRoute;
